<template>
    <div id="WorkFlowSandbox">
        <div class="header">
            <div class="header-icon">
                <i class="material-icons">delete_outline</i>
            </div>
            <div class="header-text">
                <h1>{{ $t('task.workflowSandbox.title') }}</h1>
                <p>{{ $t('task.workflowSandbox.description') }}</p>
            </div>
        </div>

        <div v-if="loading" class="workflows-loading">
            <div v-for="skltn in 3" class="workflow workflow-skeleton" :key="skltn">
                <a-skeleton :active="true" :paragraph="{ rows: 3 }" />
            </div>
        </div>

        <div v-else-if="deletedWorkflows.length === 0" class="empty-state">
            <i class="material-icons">delete_outline</i>
            <p>{{ $t('task.workflowSandbox.emptyState') }}</p>
        </div>

        <div v-else class="workflows">
            <div
                class="workflow workflow-deleted"
                v-for="workflow in deletedWorkflows"
                :key="workflow._id || workflow.id"
            >
                <div class="workflow-header">
                    <div class="workflow-title">{{ workflow.title || $t('task.workflowSandbox.unnamedWorkflow') }}</div>
                    <a-popover trigger="click" class="workflow-menu-content">
                        <div slot="content">
                            <div
                                class="workflow-menu-popover-items"
                                @click="restoreWorkflow(workflow)"
                            >
                                <span><a-icon type="undo" class="px-1" /></span>
                                <span>{{ $t('task.workflowSandbox.restore') }}</span>
                            </div>
                            <div
                                class="workflow-menu-popover-items workflow-menu-popover-items-danger"
                                @click="permanentlyDeleteWorkflow(workflow)"
                            >
                                <span><a-icon type="delete" class="px-1" /></span>
                                <span>{{ $t('task.workflowSandbox.permanentlyDelete') }}</span>
                            </div>
                        </div>
                        <a-button
                            class="workflow-menu-btn"
                            @click="(e) => e.stopPropagation()"
                        >
                            <span class="material-icons">more_vert</span>
                        </a-button>
                    </a-popover>
                </div>
                <div class="workflow-content">
                    <div class="workflow-description" v-if="workflow.description">
                        {{ workflow.description }}
                    </div>
                    <div class="workflow-meta">
                        <div class="meta-item">
                            <i class="material-icons">schedule</i>
                            <span>{{ formatDeletedDate(workflow.trashedAt) }}</span>
                        </div>
                        <div class="meta-item" v-if="workflow.trashedBy">
                            <i class="material-icons">person</i>
                            <span>{{ getDeletedByText(workflow.trashedBy) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { listDeletedWorkflows, restoreWorkflow, permanentlyDeleteWorkflow } from "../../../../api/tasks";
import moment from "moment";

export default {
    name: "WorkFlowSandbox",
    data() {
        return {
            loading: true,
            deletedWorkflows: [],
        };
    },
    methods: {
        formatDeletedDate(date) {
            if (!date) return this.$t("task.workflowSandbox.unknown");
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
        getDeletedByText(deletedBy) {
            if (!deletedBy) return this.$t("task.workflowSandbox.unknown");
            if (typeof deletedBy === "object" && deletedBy.name) {
                return deletedBy.name;
            }
            if (typeof deletedBy === "object" && deletedBy.email) {
                return deletedBy.email;
            }
            return this.$t("task.workflowSandbox.user");
        },
        async restoreWorkflow(workflow) {
            this.$confirm({
                title: this.$t("task.workflowSandbox.restoreConfirmTitle"),
                content: this.$t("task.workflowSandbox.restoreConfirmContent", {
                    title: workflow.title || this.$t("task.workflowSandbox.unnamedWorkflow"),
                }),
                onOk: async () => {
                    try {
                        await restoreWorkflow(workflow._id || workflow.id);
                        this.$message.success(this.$t("task.workflowSandbox.restoreSuccess"));
                        await this.loadDeletedWorkflows();
                        await this.$store.dispatch("tasks/getWorkFlows");
                    } catch (error) {
                        console.error("Restore error:", error);
                        this.$message.error(
                            error.response && error.response.data && error.response.data.error
                                ? error.response.data.error
                                : this.$t("task.workflowSandbox.restoreFailed")
                        );
                    }
                },
            });
        },
        async permanentlyDeleteWorkflow(workflow) {
            this.$confirm({
                title: this.$t("task.workflowSandbox.deleteConfirmTitle"),
                content: this.$t("task.workflowSandbox.deleteConfirmContent", {
                    title: workflow.title || this.$t("task.workflowSandbox.unnamedWorkflow"),
                }),
                okText: this.$t("task.workflowSandbox.deleteOkText"),
                okType: "danger",
                cancelText: this.$t("task.workflowSandbox.cancel"),
                onOk: async () => {
                    try {
                        await permanentlyDeleteWorkflow(workflow._id || workflow.id);
                        this.$message.success(this.$t("task.workflowSandbox.deleteSuccess"));
                        await this.loadDeletedWorkflows();
                    } catch (error) {
                        console.error("Permanently delete error:", error);
                        this.$message.error(
                            error.response && error.response.data && error.response.data.error
                                ? error.response.data.error
                                : this.$t("task.workflowSandbox.deleteFailed")
                        );
                    }
                },
            });
        },
        async loadDeletedWorkflows() {
            try {
                this.loading = true;
                const data = await listDeletedWorkflows();
                this.deletedWorkflows = data || [];
            } catch (error) {
                console.error("Load deleted workflows error:", error);
                this.$message.error(this.$t("task.workflowSandbox.loadError"));
                this.deletedWorkflows = [];
            } finally {
                this.loading = false;
            }
        },
    },
    async mounted() {
        await this.loadDeletedWorkflows();
    },
};
</script>
<style lang="scss" scoped>
#WorkFlowSandbox {
    width: 100%;
    padding: 1.5rem;
    box-sizing: border-box;

    .header {
        display: flex;
        align-items: flex-start;
        padding: 1.5rem;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        margin-bottom: 1.5rem;
        width: 100%;
        box-sizing: border-box;

        .header-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background: #f3f4f6;
            border-radius: 6px;
            margin-right: 1rem;
            flex-shrink: 0;

            .material-icons {
                font-size: 20px;
                color: #6b7280;
            }
        }

        .header-text {
            flex: 1;
            min-width: 0;

            h1 {
                font-size: 1.25rem;
                font-weight: 600;
                color: #111827;
                margin: 0 0 0.5rem 0;
            }

            p {
                font-size: 0.875rem;
                color: #6b7280;
                margin: 0;
                line-height: 1.5;
            }
        }
    }

    .workflows-loading {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;

        .workflow-skeleton {
            width: 280px;
            height: 200px;
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 1rem;
        }
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 4rem 2rem;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        text-align: center;

        .material-icons {
            font-size: 64px;
            color: #d1d5db;
            margin-bottom: 1rem;
        }

        p {
            font-size: 1rem;
            color: #6b7280;
            margin: 0;
        }
    }

    .workflows {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;

        .workflow {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            width: 280px;
            min-width: 250px;
            max-width: 280px;
            display: flex;
            flex-direction: column;
            transition: all 0.2s;
            position: relative;

            &:hover {
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                border-color: #d1d5db;
            }

            &.workflow-deleted {
                opacity: 0.85;
                border-left: 3px solid #ef4444;
            }

            .workflow-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                padding: 1rem 1rem 0.5rem 1rem;
                border-bottom: 1px solid #f3f4f6;

                .workflow-title {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #111827;
                    flex: 1;
                    margin-right: 0.5rem;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .workflow-menu-btn {
                    border: none;
                    background: transparent;
                    padding: 0.25rem;
                    min-width: auto;
                    height: auto;
                    box-shadow: none;

                    .material-icons {
                        font-size: 20px;
                        color: #6b7280;
                    }

                    &:hover {
                        background: #f3f4f6;
                    }
                }
            }

            .workflow-content {
                padding: 1rem;
                flex: 1;
                display: flex;
                flex-direction: column;

                .workflow-description {
                    font-size: 0.875rem;
                    color: #6b7280;
                    margin-bottom: 1rem;
                    line-height: 1.5;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                .workflow-meta {
                    margin-top: auto;
                    padding-top: 0.75rem;
                    border-top: 1px solid #f3f4f6;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;

                    .meta-item {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        font-size: 0.75rem;
                        color: #9ca3af;

                        .material-icons {
                            font-size: 16px;
                        }
                    }
                }
            }
        }
    }
}

.workflow-menu-popover-items {
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        color: #1890ff;
        background: #f0f7ff;
    }

    &.workflow-menu-popover-items-danger {
        &:hover {
            color: #ff4d4f;
            background: #fff1f0;
        }
    }
}
</style>
